import {
  LayoutDashboard,
  CreditCard,
  Wallet,
  Users,
  BarChart3,
  Gamepad2,
  Settings,
  ClipboardList,
  UserCog,
  Shield,
  TrendingUp,
  Bell,
  FileText,
  MessageSquare,
  Heart,
  MessageCircle,
  Activity,
  Target,
  Mic,
  ListChecks,
  GraduationCap,
  BookOpen,
  HelpCircle,
  FolderOpen,
  Flame,
  MessageCircleQuestion,
  AlertTriangle,
  Phone,
  Smartphone,
  Building2,
  Package,
  Stethoscope,
  ShieldCheck,
  FlaskConical,
  PartyPopper,
  QrCode,
  MessagesSquare,
  Star,
  ListVideo,
  UserRound,
  Tags,
  MonitorPlay,
  Route,
} from 'lucide-react'

import { NavigationConfig } from '@/types/layout'

/**
 * Application navigation configuration
 * Defines the sidebar navigation menu structure
 */
export const navigationConfig: NavigationConfig = {
  items: [
    {
      id: 'dashboard',
      label: 'Tổng quan',
      icon: LayoutDashboard,
      path: '/dashboard',
      requiredPermissions: ['DASHBOARD_VIEW'],
    },

    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics/events',
      requiredPermissions: ['GA4_ANALYTICS_VIEW', 'POSTHOG_ANALYTICS_VIEW'],
    },

    {
      id: 'whitelist-management',
      label: 'Quản lý Whitelist',
      icon: ListChecks,
      path: '/whitelist',
      requiredPermissions: ['WHITELISTS_VIEW'],
    },

    {
      id: 'waitlist-management',
      label: 'Quản lý Waitlist',
      icon: ClipboardList,
      path: '/waitlist-registrations',
      requiredPermissions: ['TRIAL_REGISTRATIONS_VIEW'],
    },

    {
      id: 'tokens',
      label: 'Quản lý Token',
      icon: Activity,
      path: '/tokens',
      requiredPermissions: ['TOKEN_USAGE_VIEW'],
    },
    {
      id: 'gamification',
      label: 'Quản lý Gamification',
      icon: Gamepad2,
      path: '',
      children: [
        {
          id: 'gamification-missions',
          label: 'Quản lý Nhiệm vụ',
          icon: Target,
          path: '/gamification/missions',
          requiredPermissions: ['MISSION_REWARDS_VIEW'],
        },
        {
          id: 'gamification-shop-categories',
          label: 'Shop - Danh mục',
          icon: FolderOpen,
          path: '/gamification/shop/categories',
          requiredPermissions: ['SHOP_CATEGORY_VIEW'],
        },
        {
          id: 'gamification-shop-products',
          label: 'Shop - Sản phẩm',
          icon: FolderOpen,
          path: '/gamification/shop/products',
          requiredPermissions: ['SHOP_PRODUCT_VIEW'],
        },
        // Voucher do QLBH (HTVHKD) quản lý, CMS không quản lý voucher nữa.
        // Màn hình và API vẫn còn để tra cứu; bỏ comment để hiện lại menu.
        // {
        //   id: 'gamification-vouchers',
        //   label: 'Voucher - Mã voucher',
        //   icon: FolderOpen,
        //   path: '/gamification/vouchers',
        //   requiredPermissions: ['VOUCHERS_VIEW'],
        // },
        {
          id: 'gamification-redemptions',
          label: 'Lịch sử đổi quà',
          icon: FolderOpen,
          path: '/gamification/redemptions',
          requiredPermissions: ['REDEMPTIONS_VIEW'],
        },
        {
          id: 'gamification-game-items',
          label: 'Vật phẩm Game',
          icon: FolderOpen,
          path: '/gamification/game-items',
          requiredPermissions: ['GAME_ITEMS_VIEW'],
        },
        {
          id: 'gamification-tree-care-action-configs',
          label: 'Tree Care - Hành động chăm sóc',
          icon: FolderOpen,
          path: '/gamification/tree-care-action-configs',
          requiredPermissions: ['TREE_CARE_ACTION_VIEW'],
        },
        {
          id: 'gamification-tree-growth-stage-configs',
          label: 'Tree Growth - Giai đoạn phát triển',
          icon: FolderOpen,
          path: '/gamification/tree-growth-stage-configs',
          requiredPermissions: ['TREE_GROWTH_STAGE_VIEW'],
        },
        {
          id: 'gamification-tree-harvest-rewards-configs',
          label: 'Tree Harvest - Thưởng thu hoạch',
          icon: FolderOpen,
          path: '/gamification/tree-harvest-rewards-configs',
          requiredPermissions: ['TREE_HARVEST_REWARDS_VIEW'],
        },
        {
          id: 'gamification-mission-item-rewards-configs',
          label: 'Mission Item Rewards - Thưởng vật phẩm nhiệm vụ',
          icon: FolderOpen,
          path: '/gamification/mission-item-rewards-configs',
          requiredPermissions: ['MISSION_REWARDS_VIEW'],
        },
        {
          id: 'gamification-user-monitoring',
          label: 'Giám sát Người dùng',
          icon: Activity,
          path: '/gamification/user-monitoring',
          requiredPermissions: ['GAMEFI_MGMT_VIEW'],
        },
        {
          id: 'gamification-app-events',
          label: 'Sự kiện trong app',
          icon: PartyPopper,
          path: '/gamification/app-events',
          requiredPermissions: ['APP_EVENTS_VIEW'],
        },
        {
          id: 'gamification-daily-streak-rewards-config',
          label: 'Cấu hình Streak',
          icon: Flame,
          path: '/gamification/daily-streak-rewards-config',
          requiredPermissions: ['DAILY_STREAK_VIEW'],
        },
      ],
    },
    {
      id: 'surveys',
      label: 'Quản lý Khảo sát',
      icon: ClipboardList,
      path: '',
      children: [
        {
          id: 'survey-topics',
          label: 'Khảo sát',
          icon: ClipboardList,
          path: '/surveys/topics',
          requiredPermissions: ['SURVEYS_VIEW'],
        },
        {
          id: 'survey-questions',
          label: 'Câu hỏi khảo sát',
          icon: ClipboardList,
          path: '/surveys/questions',
          requiredPermissions: ['SURVEYS_VIEW'],
        },
      ],
    },
    {
      id: 'education',
      label: 'Quản lý Giáo dục',
      icon: GraduationCap,
      path: '',
      children: [
        {
          id: 'edu-categories',
          label: 'Chủ đề',
          icon: FolderOpen,
          path: '/education/categories',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-contents',
          label: 'Nội dung',
          icon: BookOpen,
          path: '/education/contents',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-quizzes',
          label: 'Câu hỏi',
          icon: HelpCircle,
          path: '/education/quizzes',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-series',
          label: 'Series',
          icon: ListVideo,
          path: '/education/series',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-journeys',
          label: 'Lộ trình',
          icon: Route,
          path: '/education/journeys',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-experts',
          label: 'Chuyên gia',
          icon: UserRound,
          path: '/education/experts',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-tags',
          label: 'Mục tiêu & đối tượng',
          icon: Tags,
          path: '/education/tags',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-feed-preview',
          label: 'Xem trước feed',
          icon: MonitorPlay,
          path: '/education/feed-preview',
          requiredPermissions: ['EDU_CONTENT_VIEW'],
        },
        {
          id: 'edu-comments',
          label: 'Bình luận',
          icon: MessagesSquare,
          path: '/education/comments',
          requiredPermissions: ['EDU_COMMENT_VIEW'],
        },
      ],
    },
    {
      id: 'hospitals',
      label: 'Quản lý Bệnh viện',
      icon: Building2,
      path: '/hospitals',
      requiredPermissions: ['HOSPITALS_VIEW'],
    },
    {
      id: 'doctors',
      label: 'Quản lý Bác sĩ',
      icon: Stethoscope,
      path: '/doctors',
      requiredPermissions: ['DOCTORS_VIEW'],
    },
    {
      id: 'kdoctor',
      label: 'K-Doctor',
      icon: ShieldCheck,
      path: '',
      children: [
        {
          id: 'kdoctor-accounts',
          label: 'Tài khoản K-Doctor',
          icon: UserCog,
          path: '/kdoctor/accounts',
          requiredPermissions: ['PORTAL_ACCOUNTS_VIEW'],
        },
        {
          id: 'kdoctor-departments',
          label: 'Khoa/phòng',
          icon: Building2,
          path: '/kdoctor/departments',
          requiredPermissions: ['PORTAL_DEPARTMENTS_VIEW'],
        },
        {
          id: 'kdoctor-interactions',
          label: 'Dữ liệu tương tác thuốc',
          icon: FlaskConical,
          path: '/kdoctor/interactions',
          requiredPermissions: ['PORTAL_INTERACTIONS_VIEW'],
        },
        {
          id: 'kdoctor-scribe-demos',
          label: 'Demo Thư ký y khoa',
          icon: QrCode,
          path: '/kdoctor/scribe-demos',
          requiredPermissions: ['PORTAL_ACCOUNTS_VIEW'],
        },
        {
          id: 'kdoctor-scribe-lab',
          label: 'Thử nghiệm Thư ký y khoa',
          icon: FlaskConical,
          path: '/kdoctor/scribe-lab',
          requiredPermissions: ['AGENT_SETTINGS_VIEW'],
        },
      ],
    },
    {
      id: 'kbiz',
      label: 'K-Biz',
      icon: Building2,
      path: '',
      children: [
        {
          id: 'kbiz-organizations',
          label: 'Doanh nghiệp K-Biz',
          icon: Building2,
          path: '/kbiz/organizations',
          requiredPermissions: ['KBIZ_ORGS_VIEW'],
        },
        {
          id: 'kbiz-packages',
          label: 'Danh mục gói',
          icon: Package,
          path: '/kbiz/packages',
          requiredPermissions: ['KBIZ_ORGS_VIEW'],
        },
        {
          id: 'kbiz-feedbacks',
          label: 'Đánh giá đợt khám',
          icon: Star,
          path: '/kbiz/feedbacks',
          requiredPermissions: ['KBIZ_ORGS_VIEW'],
        },
      ],
    },
    {
      id: 'user-management',
      label: 'Quản lý Người dùng',
      icon: Users,
      path: '',
      children: [
        {
          id: 'user-management-users',
          label: 'Người dùng',
          icon: UserCog,
          path: '/user-management/users',
          requiredPermissions: ['USERS_VIEW'],
        },
        // {
        //   id: 'user-management-admins',
        //   label: 'Quản trị viên',
        //   icon: Shield,
        //   path: '/user-management/admins',
        // },
        {
          id: 'user-management-feedbacks',
          label: 'Quản lý Phản hồi',
          icon: MessageCircle,
          path: '/user-management/feedbacks',
          requiredPermissions: ['FEEDBACKS_VIEW'],
        },
      ],
    },
    {
      id: 'admin-management',
      label: 'Quản trị hệ thống',
      icon: Shield,
      path: '',
      requiredPermissions: ['ADMIN_USERS_VIEW', 'PERMISSION_GROUPS_VIEW'],
      children: [
        {
          id: 'admin-management-admins',
          label: 'Tài khoản admin',
          icon: UserCog,
          path: '/user-management/admins',
          requiredPermissions: ['ADMIN_USERS_VIEW'],
        },
        {
          id: 'admin-management-rbac-groups',
          label: 'Nhóm quyền',
          icon: Shield,
          path: '/admin-management/rbac/groups',
          requiredPermissions: ['PERMISSION_GROUPS_VIEW'],
        },
      ],
    },
    {
      id: 'agent-management',
      label: 'Quản lý Agent',
      icon: MessageSquare, // Using MessageSquare as a placeholder, could be Bot if available or imported
      path: '',
      children: [
        {
          id: 'agent-models',
          label: 'Model',
          icon: Settings,
          path: '/agent-management/models',
          requiredPermissions: ['AGENT_MODELS_VIEW'],
        },
        {
          id: 'agent-settings',
          label: 'Agent',
          icon: UserCog,
          path: '/agent-management/settings',
          requiredPermissions: ['AGENT_SETTINGS_VIEW'],
        },
      ],
    },
    {
      id: 'schedule',
      label: 'Quản lý lịch trình',
      icon: ClipboardList,
      path: '/schedule',
      requiredPermissions: ['SCHEDULE_JOBS_VIEW'],
    },
    {
      id: 'content',
      label: 'Quản lý Nội dung',
      icon: FileText,
      path: '',
      children: [
        {
          id: 'legal-documents',
          label: 'Tài liệu pháp lý',
          icon: FileText,
          path: '/content/legal-documents',
          requiredPermissions: ['CONTENT_MGMT_VIEW'],
        },
        {
          id: 'locations',
          label: 'Địa điểm',
          icon: FileText,
          path: '/content/locations',
          requiredPermissions: ['CONTENT_MGMT_VIEW'],
        },
        {
          id: 'medical-conditions',
          label: 'Bệnh nền',
          icon: Heart,
          path: '/content/medical-conditions',
          requiredPermissions: ['MEDICAL_CONDITIONS_VIEW'],
        },
      ],
    },
    {
      id: 'onboarding',
      label: 'Quản lý Onboarding',
      icon: Smartphone,
      path: '/onboarding',
      requiredPermissions: ['ONBOARDING_VIEW'],
    },
    {
      id: 'social',
      label: 'Quản lý Mạng xã hội',
      icon: MessageSquare,
      path: '',
      children: [
        {
          id: 'post-moderation',
          label: 'Kiểm duyệt bài viết',
          icon: MessageSquare,
          path: '/social/posts',
          requiredPermissions: ['SOCIAL_MGMT_VIEW'],
        },
        {
          id: 'charity-campaigns',
          label: 'Chiến dịch từ thiện',
          icon: Heart,
          path: '/social/charity',
          requiredPermissions: ['SOCIAL_MGMT_VIEW'],
        },
      ],
    },
    {
      id: 'system-config',
      label: 'Quản lý Cấu hình hệ thống',
      icon: Settings,
      path: '',
      children: [
        {
          id: 'app-configs',
          label: 'Cấu hình chung',
          icon: Settings,
          path: '/system-config/app-configs',
          requiredPermissions: ['APP_CONFIG_VIEW'],
        },
        {
          id: 'app-settings',
          label: 'Cài đặt ứng dụng',
          icon: Settings,
          path: '/system-config/app-settings',
          requiredPermissions: ['SYS_CONFIG_VIEW'],
        },
        // {
        //   id: 'notification-templates',
        //   label: 'Mẫu thông báo',
        //   icon: Bell,
        //   path: '/system-config/notification-templates',
        // },
        // {
        //   id: 'localization',
        //   label: 'Đa ngôn ngữ',
        //   icon: FileText,
        //   path: '/system-config/localization',
        // },
        {
          id: 'tts-voices',
          label: 'Cấu hình giọng nói',
          icon: Mic,
          path: '/system-config/tts-voices',
          requiredPermissions: ['VOICE_CONFIG_VIEW'],
        },
      ],
    },
    {
      id: 'faq-support',
      label: 'Trợ giúp & Phản hồi',
      icon: MessageCircleQuestion,
      path: '',
      children: [
        {
          id: 'faq-categories',
          label: 'Danh mục FAQ',
          icon: FolderOpen,
          path: '/faq/categories',
          requiredPermissions: ['FAQ_CATEGORY_VIEW'],
        },
        {
          id: 'faq-items',
          label: 'Câu hỏi FAQ',
          icon: HelpCircle,
          path: '/faq/items',
          requiredPermissions: ['FAQ_ITEMS_VIEW'],
        },
        {
          id: 'support-questions',
          label: 'Câu hỏi hỗ trợ',
          icon: MessageCircle,
          path: '/support/questions',
          requiredPermissions: ['SUPPORT_QUESTIONS_VIEW'],
        },
        {
          id: 'user-feedback',
          label: 'Phản hồi người dùng',
          icon: MessageSquare,
          path: '/feedback',
          requiredPermissions: ['FEEDBACKS_VIEW'],
        },
      ],
    },
    {
      id: 'payment-subscriptions',
      label: 'Tra cứu Đăng ký',
      icon: CreditCard,
      path: '/payment-subscriptions',
      requiredPermissions: ['SUBSCRIPTIONS_VIEW'],
    },
    {
      id: 'tammi-payments',
      label: 'Giao dịch Tammi',
      icon: Wallet,
      path: '/tammi-payments',
      requiredPermissions: ['SUBSCRIPTIONS_VIEW'],
    },
    {
      id: 'notifications',
      label: 'Quản lý Thông báo',
      icon: Bell,
      path: '',
      children: [
        // {
        //   id: 'notifications-dashboard',
        //   label: 'Tổng quan',
        //   icon: BarChart3,
        //   path: '/notifications',
        // },
        // {
        //   id: 'notifications-broadcast',
        //   label: 'Gửi thông báo',
        //   icon: Bell,
        //   path: '/notifications/broadcast',
        // },
        // {
        //   id: 'notifications-scheduled',
        //   label: 'Đã lên lịch',
        //   icon: ClipboardList,
        //   path: '/notifications/scheduled',
        // },
        // {
        //   id: 'notifications-status',
        //   label: 'Trạng thái gửi',
        //   icon: BarChart3,
        //   path: '/notifications/delivery-status',
        // },
        {
          id: 'notifications-push',
          label: 'Thông báo đẩy (FCM)',
          icon: Bell,
          path: '/notifications/push',
          requiredPermissions: ['FCM_NOTIFICATIONS_VIEW'],
        },
      ],
    },
    {
      id: 'sos-management',
      label: 'Quản lý SOS',
      icon: AlertTriangle,
      path: '',
      children: [
        {
          id: 'sos-dashboard',
          label: 'Tổng quan',
          icon: BarChart3,
          path: '/sos',
          requiredPermissions: ['SOS_EVENTS_VIEW'],
        },
        {
          id: 'sos-events',
          label: 'Sự kiện SOS',
          icon: AlertTriangle,
          path: '/sos/events',
          requiredPermissions: ['SOS_EVENTS_VIEW'],
        },
        {
          id: 'sos-notifications',
          label: 'Nhật ký thông báo',
          icon: Bell,
          path: '/sos/notifications',
          requiredPermissions: ['SOS_EVENTS_VIEW'],
        },
        {
          id: 'sos-first-aid',
          label: 'Nội dung sơ cứu',
          icon: Phone,
          path: '/sos/first-aid',
          requiredPermissions: ['FIRST_AID_VIEW'],
        },
        {
          id: 'sos-first-aid-categories',
          label: 'Danh mục sơ cứu',
          icon: Phone,
          path: '/sos/first-aid/categories',
          requiredPermissions: ['FIRST_AID_CATEGORY_VIEW'],
        },
      ],
    },

    // {
    //   id: 'reports',
    //   label: 'Quản lý Báo cáo & Phân tích',
    //   icon: TrendingUp,
    //   path: '',
    //   children: [
    //     {
    //       id: 'bi-dashboard',
    //       label: 'BI Dashboard',
    //       icon: BarChart3,
    //       path: '/reports/bi-dashboard',
    //     },
    //     {
    //       id: 'custom-reports',
    //       label: 'Báo cáo tùy chỉnh',
    //       icon: FileText,
    //       path: '/reports/custom',
    //     },
    //     {
    //       id: 'scheduled-reports',
    //       label: 'Báo cáo định kỳ',
    //       icon: ClipboardList,
    //       path: '/reports/scheduled',
    //     },
    //     {
    //       id: 'analytics',
    //       label: 'Phân tích',
    //       icon: BarChart3,
    //       path: '/reports/analytics',
    //     },
    //     {
    //       id: 'predictive',
    //       label: 'Dự đoán',
    //       icon: TrendingUp,
    //       path: '/reports/predictive',
    //     },
    //   ],
    // },
  ],
}
